var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import T from '../../Utils/Locale';
import AdviceDates from './AdviceDates';
import AdviceTimes from './AdviceTimes';
import { addToBasket } from '../Purchase/API';
import PurchaseType, { getDefaultPurchaseType } from '../Purchase/PurchaseType';
import Button from '../../Components/ComponentButton';
import { formatDate } from '../../Utils/DateFormatter';
import PriceTag from './PriceTag';
import { getWeekNumber, getDatesOfWeek, getNextWeekNumberAndYear } from './utilities';
import { isDateGreaterThanToday } from '../Purchase/BookingSystem';
function AdviceBooking({ productType, document = null }) {
    var _a;
    const [error, setError] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [isBookingsLoaded, setBookingsLoaded] = useState(false);
    const [bookingDates, setBookingDates] = useState({});
    const [timePeriod, setTimePeriod] = useState({
        week: getWeekNumber(new Date()),
        year: new Date().getFullYear()
    });
    // Figure which purchase type to fetch dates for. If a document exists with a purchase, then fetch dates for that,
    // else fetch dates for the default purchase type.
    const purchaseType = !document || !document.isPurchased || document.purchasedAsType === PurchaseType.DIGITAL
        ? getDefaultPurchaseType()
        : document.purchasedAsType;
    useEffect(() => {
        const { year, week } = timePeriod;
        const weekdays = getDatesOfWeek(week, year);
        const fromDate = weekdays[0];
        const toDate = weekdays[4];
        // Don't fetch expired bookings
        if (toDate.getTime() < new Date().getTime()) {
            setTimePeriod(getNextWeekNumberAndYear(timePeriod.week, timePeriod.year));
            return;
        }
        // Don't fetch specific year/week bookings if they were fetched before
        const timePeriodKey = `${week}-${year}`;
        if (bookingDates[timePeriodKey]) {
            selectDate(bookingDates[timePeriodKey][0].start);
            return;
        }
        const fromParam = `${fromDate.getDate()}-${fromDate.getMonth() + 1}-${year}`;
        const toParam = `${toDate.getDate()}-${toDate.getMonth() + 1}-${year}`;
        if (isBookingsLoaded) {
            setBookingsLoaded(false);
        }
        axios.get(`/api/booking/available?purchaseType=${purchaseType}&productTypes=${productType}&from=${fromParam}&to=${toParam}`)
            .then(({ data }) => {
            const notExpiredBookings = data.filter(booking => isDateGreaterThanToday(new Date(booking.start)));
            if (notExpiredBookings.length === 0) {
                setTimePeriod(getNextWeekNumberAndYear(timePeriod.week, timePeriod.year));
                return;
            }
            setBookingDates(Object.assign(Object.assign({}, bookingDates), { [timePeriodKey]: notExpiredBookings }));
            setBookingsLoaded(true);
            selectDate(notExpiredBookings[0].start);
        })
            .catch(() => {
            setBookingsLoaded(true);
        });
    }, [timePeriod, productType, purchaseType]);
    const currentTimePeriodBookingDates = (_a = bookingDates[`${timePeriod.week}-${timePeriod.year}`]) !== null && _a !== void 0 ? _a : [];
    return (_jsxs("div", Object.assign({ className: 'flex flex-col overflow-hidden h-full' }, { children: [_jsxs("div", Object.assign({ className: 'flex-grow' }, { children: [!error && (_jsx("h2", Object.assign({ className: 'tailwind-reset text-base font-medium mb-5 md:pt-4' }, { children: T('dashboard.advice.headline', { type: T(`purchase_type.${purchaseType}`).toLowerCase() }) }))), error && (_jsx("p", Object.assign({ className: 'text-red-700' }, { children: error }))), selectedDate && (_jsx(AdviceDates, { availableDates: currentTimePeriodBookingDates, selectedDate: selectedDate, onDateSelect: setSelectedDate, 
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        onTimePeriodChange: isBookingsLoaded ? setTimePeriod : () => { }, timePeriod: timePeriod })), !isBookingsLoaded && (_jsx("p", Object.assign({ className: 'mt-4' }, { children: T('dashboard.fetching_dates') }))), (isBookingsLoaded && currentTimePeriodBookingDates.length === 0) && (_jsx("p", Object.assign({ className: 'text-red-700' }, { children: T('dashboard.fetching_dates_error') })))] })), _jsx("div", Object.assign({ className: 'flex-grow mb-3 mt-3 overflow-hidden' }, { children: isBookingsLoaded && (_jsx(AdviceTimes, { bookingDates: currentTimePeriodBookingDates, selectedStartTime: selectedStartTime, selectedDate: selectedDate, setSelectedStartTime: setSelectedStartTime })) })), _jsx("div", Object.assign({ className: 'actions mt-auto flex-shrink-0' }, { children: _jsxs("div", Object.assign({ className: 'flex flex-row items-center' }, { children: [(!document || !document.isPurchased || document.purchasedAsType === PurchaseType.DIGITAL) && (_jsx(PriceTag, { productType: productType, purchaseType: purchaseType, document: document })), _jsx(Button, { id: 'bookingButton', onClick: bookDate, classes: 'ml-auto xs:text-base text-lg tailwind-reset btn border-solid bg-white border border-dark-teal text-dark-teal xs:px-8 px-10 hover:text-white active:shadow-outline', title: document && document.isPurchased && document.purchasedAsType === PurchaseType.DIGITAL ? T('dashboard.advice.upgrade') : T('dashboard.advice.book'), isLoading: isLoading })] })) }))] })));
    function selectDate(date) {
        const dateToSelect = new Date(date);
        dateToSelect.setHours(0, 0, 0, 0);
        setSelectedDate(dateToSelect);
    }
    function bookDate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!selectedDate || !selectedStartTime) {
                return;
            }
            const selectedTimeSlot = new Date(selectedStartTime.start);
            const parameters = new URLSearchParams({ employee: selectedStartTime.resources[0].id });
            setLoading(true);
            if (document && document.isPurchased && document.purchasedAsType !== PurchaseType.DIGITAL) {
                try {
                    parameters.set('date', formatDate(selectedTimeSlot));
                    yield axios.post(`/api/booking/document/${document.id}?${parameters.toString()}`);
                    window.location.reload();
                }
                catch (error) {
                    if (error.response && error.response.status && error.response.status === 503) {
                        setError(T('dashboard.booking_occupied'));
                    }
                    else {
                        setError(T('dashboard.booking_error'));
                    }
                    setLoading(false);
                }
            }
            else {
                parameters.set('date', selectedTimeSlot);
                if (!document || !document.isPurchased) {
                    yield addToBasket(productType, purchaseType);
                }
                // If the document should be upgrade, we'll redirect to upgrade, otherwise we'll redirect to the basket
                window.location.href = !document || !document.isPurchased
                    ? `basket?${parameters}`
                    : `/my-documents/upgrade/${document.id}?${parameters}`;
            }
        });
    }
}
AdviceBooking.propTypes = {
    productType: PropTypes.string.isRequired,
    document: PropTypes.shape({
        id: PropTypes.number.isRequired,
        isPurchased: PropTypes.bool.isRequired,
        purchasedAsType: PropTypes.string
    })
};
export default AdviceBooking;
